/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { snackNotifications } from '../notification/SnackBarUtils';
import { PageContainerWithBreadcrumbs } from '../PageContainer/PageContainerWithBreadcrumbs';
import { downloadAsJsonFile } from '../utils';
import styles from './AdminFhirDownloadAnonymous.module.scss';
import { doDownloadGroup, doDownloadMrns } from './adminFhirDownload_utils';

export function AdminFhirDownloadAnonymous() {
  const [mrns, setMrns] = useState<string>('');
  const [groupId, setGroupId] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);
  const abortController = useMemo(() => new AbortController(), []);
  const [statusMessage, setStatusMessage] = useState<string>('');

  const [datasetCache, setDatasetCache] = useState<any[]>();

  const doDownload = useCallback(async () => {
    setProgress(1);
    const startTime = Date.now();
    try {
      const { data, errors } = groupId
        ? await doDownloadGroup(groupId, 'DiagnosticReport', abortController.signal, setProgress)
        : await doDownloadMrns(mrns, datasetCache, setDatasetCache, abortController.signal, setProgress, setMrns);
      if (Array.isArray(data)) {
        snackNotifications.success(`Downloaded ${data?.length || 0}, ${errors} errors`);
      } else {
        snackNotifications.success(`Downloaded, ${errors} errors`);
      }
      setProgress(0);
      setStatusMessage(
        `Downloaded, ${errors} errors, took ${((Date.now() - startTime) / (1000 * 60)).toFixed(0)} minutes`
      );
      // Now we need to download the data
      if (data && (!Array.isArray(data) || data?.length > 0))
        downloadAsJsonFile(data, `musc-patients-${new Date().toISOString()}.json`);
    } catch (e) {
      console.error('Error downloading patients:', e);
      snackNotifications.error('Error downloading patients');
      setProgress(0);
    }
  }, [groupId, mrns, datasetCache, abortController.signal]);

  const doCancel = useCallback(() => {
    setProgress(0);
    setStatusMessage('Download cancelled');
    abortController.abort();
  }, [abortController]);
  return (
    <PageContainerWithBreadcrumbs
      breadcrumbsProps={{
        goBackLink: '/admin',
        children: [
          <Link to="/admin" key="0">
            Admin
          </Link>,
          <span key="1">Download Anonymous FHIR Patients</span>,
        ],
      }}
    >
      <h1>Download Anonymous FHIR Patients</h1>
      <p>
        This page allows you to download the anonymized FHIR data for a set of patients in the system. This will take a
        while, and then prompt you to download a file with the data. This can be reviewed in any text editor and then
        sent to us.
      </p>
      <p>Download by Group ID:</p>
      <div>
        <TextField
          label="Group ID"
          variant="filled"
          className={styles.groupId}
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
        />
      </div>
      <p>
        Or, Download by MRNS:{' '}
        <textarea
          className={styles.textarea}
          value={mrns}
          onChange={(e) => setMrns(e.target.value)}
          placeholder="MRN1&#10;MRN2&#10;MRN3"
        ></textarea>
      </p>
      <p className={styles.progressDiv}>{progress > 0 && <LinearProgress variant="determinate" value={progress} />}</p>
      <p>
        <b>{statusMessage}</b>
      </p>
      {progress === 0 ? (
        <Button variant="contained" color="primary" disabled={!mrns && !groupId} onClick={doDownload}>
          Download
        </Button>
      ) : (
        <Button variant="contained" color="error" onClick={doCancel}>
          Cancel
        </Button>
      )}
    </PageContainerWithBreadcrumbs>
  );
}
